import { nextTick } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import Home from '../views/Home.vue';
import store from '../store/index';

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { auth: true },
  },
  {
    path: '/orders',
    name: 'Orders',
    component: () => import('../views/Orders/Orders.vue'),
    meta: { auth: true },
  },
  {
    path: '/configuration',
    component: () => import('../views/Configuration/Configuration.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: '/configuration/onboarding',
      },
      {
        path: 'api',
        name: 'API Keys - Configuration',
        component: () => import('../views/Configuration/ConfigurationAPI.vue'),
        meta: { auth: true },
      },
      {
        path: 'integrations',
        component: () => import('../views/Configuration/ConfigurationIntegrations.vue'),
        children: [
          {
            path: '',
            redirect: '/configuration/integrations/platform',
          },
          {
            path: 'platform',
            name: 'Integrations (eCommerce Platform) - Configuration',
            component: () => import('../views/Configuration/ConfigurationIntegrationsPlatform.vue'),
            meta: { auth: true },
          },
          {
            path: 'partners',
            name: 'Integrations (Partner Integrations) - Configuration',
            component: () => import('../views/Configuration/ConfigurationIntegrationsPartners.vue'),
            meta: { auth: true },
          },
          {
            path: 'oauth-brightpearl',
            name: 'Brightpearl - Integrations (Partner Integrations) - Configuration',
            component: () => import('../views/Configuration/OAuth/Brightpearl.vue'),
            meta: { auth: true },
          },
        ],
      },
      {
        path: 'frontend',
        component: () => import('../views/Configuration/ConfigurationFrontend.vue'),
        meta: { auth: true },
        children: [
          {
            path: '',
            redirect: '/configuration/frontend/core',
          },
          {
            path: 'core',
            name: 'Frontend Integration (Core script) - Configuration',
            component: () => import('../views/Configuration/ConfigurationFrontendCore.vue'),
            meta: { auth: true },
          },
          {
            path: 'pdp',
            name: 'Frontend Integration (Product page script) - Configuration',
            component: () => import('../views/Configuration/ConfigurationFrontendPDP.vue'),
            meta: { auth: true },
          },
          {
            path: 'css',
            name: 'Frontend Integration (CSS) - Configuration',
            component: () => import('../views/Configuration/ConfigurationFrontendCSS.vue'),
            meta: { auth: true },
          },
          {
            path: 'checkout',
            name: 'Frontend Integration (Checkout script) - Configuration',
            component: () => import('../views/Configuration/ConfigurationFrontendCheckout.vue'),
            meta: { auth: true },
          },
          {
            path: 'tips',
            name: 'Frontend Integration (Tips) - Configuration',
            component: () => import('../views/Configuration/ConfigurationFrontendTips.vue'),
            meta: { auth: true },
          },
        ],
      },
      {
        path: 'settings',
        component: () => import('../views/Configuration/ConfigurationSettings.vue'),
        meta: { auth: true },
        children: [
          {
            path: '',
            name: 'Settings - Configuration',
            redirect: '/configuration/settings/general',
          },
          {
            path: 'general',
            name: 'Settings (General) - Configuration',
            component: () => import('../views/Configuration/ConfigurationSettingsGeneral.vue'),
            meta: { auth: true },
          },
          {
            path: 'shipping-rates',
            redirect: '/configuration/shipping',
          },
        ],
      },
      {
        path: 'shipping',
        name: 'Shipping',
        component: () => import('../views/Configuration/ConfigurationShippingRates.vue'),
        meta: { auth: true },
      },
      {
        path: 'onboarding',
        name: 'Onboarding - Configuration',
        component: () => import('../views/Configuration/ConfigurationOnboarding.vue'),
        meta: { auth: true },
      },
    ],
  },
  {
    path: '/settings',
    component: () => import('../views/Account/Settings.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: '/settings/account',
      },
      {
        path: 'account',
        name: 'Account - Settings',
        component: () => import('../views/Account/SettingsAccount.vue'),
        meta: { auth: true },
      },
      {
        path: 'password',
        name: 'Password - Settings',
        component: () => import('../views/Account/SettingsPassword.vue'),
        meta: { auth: true },
      },
      {
        path: 'team',
        name: 'Team - Settings',
        component: () => import('../views/Account/SettingsTeam.vue'),
        meta: { auth: true },
      },
      {
        path: 'plan',
        name: 'Plan - Settings',
        component: () => import('../views/Account/SettingsBilingPlan.vue'),
        meta: { auth: true },
      },
    ],
  },
  {
    path: '/products',
    component: () => import('../views/Prices/MainLayout.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: '/products/price-lists',
      },
      {
        path: 'price-lists',
        name: 'Price Lists',
        component: () => import('../views/Prices/PriceLists.vue'),
        meta: { auth: true },
      },
      {
        path: 'price-editor/:variantId?',
        name: 'Price Editor',
        component: () => import('../views/Prices/PriceEditor.vue'),
        meta: { auth: true },
      },
    ],
  },
  {
    path: '/data-sync',
    component: () => import('../views/DataSync/Main.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'Data Sync',
        redirect: '/data-sync/products',
      },
      {
        path: 'products/:variantId?',
        name: 'Data Sync - Prices',
        component: () => import('../views/DataSync/Products.vue'),
        meta: { auth: true },
      },
      {
        path: 'customers',
        name: 'Data Sync - Customers',
        component: () => import('../views/DataSync/Customers.vue'),
        meta: { auth: true },
      },
    ],
  },
  {
    path: '/customers',
    component: () => import('../views/Customers/Main.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: '/customers/groups',
      },
      {
        path: 'groups',
        name: 'Customer Groups - Customer',
        component: () => import('../views/Customers/Groups.vue'),
        meta: { auth: true },
      },
    ],
  },
  {
    path: '/help',
    name: 'Help',
    component: () => import('../views/Help.vue'),
    meta: { auth: true },
  },
  {
    path: '/partners',
    component: () => import('../views/Partners/Main.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: '/partners/overview',
      },
      {
        path: 'overview',
        name: 'Partner Dashboard',
        component: () => import('../views/Partners/Overview.vue'),
        meta: { auth: true },
      },
      {
        path: 'details',
        name: 'Partner Referral',
        component: () => import('../views/Partners/Referrals.vue'),
        meta: { auth: true },
      },
      {
        path: 'downloads',
        name: 'Partner Resources',
        component: () => import('../views/Partners/Downloads.vue'),
        meta: { auth: true },
      },
    ],
  },
  {
    path: '/partner-register',
    name: 'Partner Register',
    component: () => import('../views/Partners/Register.vue'),
    meta: { auth: false },
  },
  {
    path: '/register-team-member',
    name: 'Register Team Member',
    component: () => import('../views/RegisterTeamMember.vue'),
    meta: { auth: false },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: { auth: false },
  },
  {
    path: '/logout',
    name: 'Logout',
    component: () => import('../views/Logout.vue'),
    meta: { auth: true },
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/Register.vue'),
    meta: { auth: false },
  },
  {
    path: '/go-register',
    name: 'Register - Sales Pipeline Redirect',
    redirect: '/register?app_registration_source=sales_pipeline_tracked_link',
    meta: { auth: false },
  },
  {
    path: '/register-invite',
    name: 'Invite to Register',
    component: () => import('../views/RegisterInvite.vue'),
    meta: { auth: false },
  },
  {
    path: '/no-team',
    name: 'No Team',
    component: () => import('../views/NoTeam.vue'),
    meta: { auth: true },
  },
  {
    path: '/forgotten-password',
    name: 'Forgotten Password',
    component: () => import('../views/ForgottenPassword.vue'),
    meta: { auth: false },
  },
  {
    path: '/reset-password/:token',
    name: 'Reset Password',
    component: () => import('../views/ResetPassword.vue'),
    meta: { auth: false },
  },
  {
    path: '/switch-account/:teamId',
    name: 'Switch Account',
    component: () => import('../views/SwitchAccount.vue'),
    meta: { auth: true },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
    meta: { auth: true },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

const DEFAULT_TITLE = 'SparkLayer Dashboard';
router.afterEach(({ name }) => {
  // Use next tick to handle router history correctly
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  nextTick(() => {
    document.title = name ? `${name} - ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  });
});

router.afterEach(() => {
  window.Intercom('update');
});

router.beforeEach(async ({ meta, path }, _from, next) => {
  const { auth } = meta;
  await store.dispatch('auth/loadUser');
  const isLoggedIn = store.getters['auth/isLoggedIn'];
  const hasTeam = store.getters['auth/hasTeam'];
  const isPartner = store.getters['auth/isPartner'];

  if (isLoggedIn && hasTeam && !isPartner) {
    await store.dispatch('env/onLoad');
  }

  if (auth && !isLoggedIn) {
    next({ name: 'Login', query: { from: window.location.pathname } });
  } else if (path !== '/no-team' && auth && !hasTeam) {
    next({ name: 'No Team' });
  } else if (!auth && isLoggedIn) {
    next({ name: 'Home' });
  }

  next();
});

export default router;
